import { FC, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient, trpc, trpcClient } from '@/lib/trpc';
import { Button } from '@deepguide-ai/ui/button';
import { Input } from '@deepguide-ai/ui/input';
import { Label } from '@deepguide-ai/ui/label';
import { Switch } from '@deepguide-ai/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@deepguide-ai/ui/select';
import type { Integration, IntegrationFeature, IntegrationProvider, ConfigModalProps } from '../types';
import { Badge } from '@deepguide-ai/ui/badge';
import { toast } from 'sonner';
import { APP_NAME } from '@/lib/env';
import { createOAuthConnectLifecycle, openOAuthConnectPopup } from '../oauth-connect-lifecycle';
const TeamsIcon: FC = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="teamsGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#6264A7" />
        <stop offset="100%" stopColor="#464775" />
      </linearGradient>
    </defs>
    <rect x="3" y="4" width="18" height="16" rx="4" fill="url(#teamsGradient)" />
    <path
      d="M9 9H11.5C12.33 9 13 9.67 13 10.5C13 11.33 12.33 12 11.5 12H10.5V15H9V9Z"
      fill="#FFFFFF"
    />
    <circle cx="16" cy="10" r="2" fill="#FFFFFF" opacity="0.85" />
  </svg>
);

const TeamsConfigModal: FC<ConfigModalProps> = ({ open, onOpenChange, integration, onComplete }) => {
  const integrationId = integration?.id;

  const settingsQuery = trpc.integrations.getMicrosoftTeamsCaptureSettings.queryOptions(
    { integrationId: integrationId || '' },
    { enabled: open && !!integrationId }
  );
  const { data: settings } = useQuery(settingsQuery);

  const updateMutation = useMutation(
    trpc.integrations.updateMicrosoftTeamsCaptureSettings.mutationOptions()
  );

  const [enabled, setEnabled] = useState(true);
  const [captureMode, setCaptureMode] = useState<'MENTIONS_ONLY' | 'KEYWORDS' | 'ALL'>('MENTIONS_ONLY');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [excludeUsers, setExcludeUsers] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [userInput, setUserInput] = useState('');
  const [redactPII, setRedactPII] = useState(true);
  const [retentionDays, setRetentionDays] = useState(0);

  useEffect(() => {
    if (settings && open) {
      setEnabled(settings.enabled ?? true);
      setCaptureMode(settings.captureMode as any);
      setKeywords(settings.keywords ?? []);
      setExcludeUsers(settings.excludeUsers ?? []);
      setRedactPII(settings.redactPII ?? true);
      setRetentionDays(settings.retentionDays ?? 0);
    }
  }, [settings, open]);

  const addKeyword = () => {
    const value = keywordInput.trim();
    if (!value) return;
    if (!keywords.includes(value)) {
      setKeywords([...keywords, value]);
    }
    setKeywordInput('');
  };

  const removeKeyword = (value: string) => setKeywords(keywords.filter(k => k !== value));

  const addUser = () => {
    const value = userInput.trim();
    if (!value) return;
    if (!excludeUsers.includes(value)) {
      setExcludeUsers([...excludeUsers, value]);
    }
    setUserInput('');
  };

  const removeUser = (value: string) => setExcludeUsers(excludeUsers.filter(u => u !== value));

  const onSave = async () => {
    if (!integrationId) {
      onOpenChange(false);
      return;
    }

    await updateMutation.mutateAsync({
      integrationId,
      enabled,
      captureMode,
      keywords,
      excludeUsers,
      redactPII,
      retentionDays,
    });

    await queryClient.invalidateQueries({
      queryKey: trpc.integrations.getMicrosoftTeamsCaptureSettings.queryKey({ integrationId })
    });

    toast.success('Teams capture settings updated');
    onOpenChange(false);
    onComplete();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Microsoft Teams Capture Settings</DialogTitle>
          <DialogDescription>Configure how Teams messages are ingested into {APP_NAME}.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="teams-enabled" className="flex flex-col">
              Enable capture
              <span className="text-sm text-muted-foreground">Master toggle for Teams ingestion</span>
            </Label>
            <Switch id="teams-enabled" checked={enabled} onCheckedChange={setEnabled} />
          </div>

          <div className="space-y-2">
            <Label>Capture mode</Label>
            <Select value={captureMode} onValueChange={value => setCaptureMode(value as any)}>
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="Select mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="MENTIONS_ONLY">Mentions only (@bot)</SelectItem>
                <SelectItem value="KEYWORDS">Keywords</SelectItem>
                <SelectItem value="ALL">All channel messages</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {captureMode === 'KEYWORDS' && (
            <div className="space-y-2">
              <Label>Keywords</Label>
              <div className="flex flex-wrap gap-2">
                {keywords.map(keyword => (
                  <Badge key={keyword} variant="secondary" className="cursor-pointer" onClick={() => removeKeyword(keyword)}>
                    {keyword} ✕
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Add keyword"
                  value={keywordInput}
                  onChange={event => setKeywordInput(event.target.value)}
                  onKeyDown={event => event.key === 'Enter' && (event.preventDefault(), addKeyword())}
                />
                <Button variant="secondary" onClick={addKeyword}>Add</Button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>Exclude users</Label>
            <div className="flex flex-wrap gap-2">
              {excludeUsers.map(user => (
                <Badge key={user} variant="outline" className="cursor-pointer" onClick={() => removeUser(user)}>
                  {user} ✕
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="User ID or UPN"
                value={userInput}
                onChange={event => setUserInput(event.target.value)}
                onKeyDown={event => event.key === 'Enter' && (event.preventDefault(), addUser())}
              />
              <Button variant="secondary" onClick={addUser}>Add</Button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Label className="flex flex-col">
              Redact PII
              <span className="text-sm text-muted-foreground">Automatically remove sensitive data before storage</span>
            </Label>
            <Switch checked={redactPII} onCheckedChange={setRedactPII} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="teams-retention">Retention (days)</Label>
            <Input
              id="teams-retention"
              type="number"
              min={0}
              max={365}
              value={retentionDays}
              onChange={event => setRetentionDays(Number(event.target.value))}
            />
            <p className="text-xs text-muted-foreground">Automatically purge Teams sources after the specified number of days.</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSave} disabled={updateMutation.isPending}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const MicrosoftTeamsProvider: IntegrationProvider = {
  id: 'MicrosoftTeams',
  name: 'Microsoft Teams',
  description: 'Ingest Microsoft Teams conversations and automatically capture threaded replies.',
  Icon: TeamsIcon,
  ConfigModal: TeamsConfigModal,

  getFeatures: (): IntegrationFeature[] => [
    {
      id: 'messages',
      name: 'Channel messages',
      description: 'Import Microsoft Teams channel posts',
      enabled: true,
    },
    {
      id: 'threads',
      name: 'Threaded replies',
      description: 'Capture threaded replies as documentation sources',
      enabled: true,
    },
    {
      id: 'webhooks',
      name: 'Graph notifications',
      description: 'Process Microsoft Graph change notifications',
      enabled: true,
    },
  ],

  handleConnect: async ({ workspaceId, features, redirectUrl, onSuccess, onFinish }) => {
    const { finish } = createOAuthConnectLifecycle(onFinish);
    try {
      const baseWebUrl = import.meta.env.VITE_WEB_URL ?? window.location.origin;
      const callbackUrl = redirectUrl ?? `${baseWebUrl}/auth-callback/MicrosoftTeams`;

      const data = await trpcClient.integrations.initMicrosoftTeamsAuth.mutate({
        workspaceId,
        features,
        redirectUrl: callbackUrl,
        providerType: 'MicrosoftTeams',
      });

      if (!data.authUrl) {
        console.error('No auth URL returned for Microsoft Teams initialization');
        finish('error');
        return;
      }

      openOAuthConnectPopup({
        authUrl: data.authUrl, windowName: 'microsoft-teams-auth', provider: 'MicrosoftTeams',
        onSuccess: result => {
          void queryClient.invalidateQueries({ queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey() });
          onSuccess?.(result);
        },
        onError: message => toast.error(message),
        onPopupBlocked: () => toast.error('OAuth popup was blocked. Allow popups and try again.'),
        onFinish: finish,
      })
    } catch (error: any) {
      console.error('Error initializing Microsoft Teams auth', error);
      toast.error(`Failed to start Microsoft Teams authentication: ${error?.message || 'Unknown error'}`);
      finish('error');
    }
  },

  onConnectComplete: async ({ code, state }) => {
    return await trpcClient.integrations.completeMicrosoftTeamsAuth.mutate({ code, state });
  },

  syncData: async (_integration: Integration) => {
    // Triggering a sync relies on webhook-driven ingestion; no-op for now.
    return Promise.resolve();
  },

  shouldDisplay: () => true,
};
